import Vue from 'vue'
import Router from 'vue-router'
import config from '@/config'
import identity from './identity'
import store from '@/store/index'
import { PROMOTION_TYPE } from '@/constants/promotion'
import { isNeedKYC } from '@/constants/payment/depositSetting'
import registerRoutes from './registerRoutes'
import { getIbtRedirectPath } from '@/util/register/getIbtRedirectPath'
import { isSuccess } from '@/util/utils'
import { endLoading } from '@/util/loading'

Vue.use(Router)

const participatedCampaignExist = campaignType =>
  store.state.promotion.participatedCampaigns.some(campaign => campaign === campaignType)

function canParticipate(CAMPAIGN_TYPE) {
  //https://stackoverflow.com/questions/8217419/how-to-determine-if-javascript-array-contains-an-object-with-an-attribute-that-e
  //https://stackoverflow.com/questions/42603909/accessing-vuex-state-when-defining-vue-router-routes
  return (
    store.state.promotion.eligibleCampaigns.filter(campaign => {
      return campaign.campaignType === CAMPAIGN_TYPE
    }).length > 0
  )
}

async function verifyPropTradingDashboardEligibility() {
  let { account, eligible, session } = store.state.propTrading

  if (eligible && (session || account)) {
    return true
  }

  const result = await store.dispatch('propTrading/actionGetEligiblePropTrading')

  if (isSuccess(result.data)) {
    ;({ account, eligible, session } = store.state.propTrading)
  }
  if (eligible && (session || account)) {
    return true
  }
  return false
}

async function verifyPropTradingEligibility() {
  let { eligible } = store.state.propTrading

  if (eligible) {
    return true
  }

  const result = await store.dispatch('propTrading/actionGetEligiblePropTrading')

  if (isSuccess(result.data)) {
    ;({ eligible } = store.state.propTrading)
  }

  if (eligible) {
    return true
  }

  return false
}

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      component: () => import('@/views/shared/Layout'),
      children: [
        {
          path: '/',
          name: '/',
          meta: {
            noShowLoading: true,
          },
        },
        {
          path: '/logout',
          name: 'logout',
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
            restrictBanner: true,
          },
        },
        ...registerRoutes,
        {
          path: '/demo',
          name: 'demo',
          component: () => import('@/views/register/Demo'),
          meta: {
            restrictBanner: true,
          },
        },
        {
          path: '/authority',
          name: 'authority',
          component: () => import('@/views/register/Authority'),
        },
        {
          path: '/vtrade/becomes-a-follower',
          name: 'vtradeFollower',
          component: resolve => require(['@/views/vtrade/VTradeFollower'], resolve),
          meta: {
            restrictBanner: true,
            authority: true,
          },
        },
        {
          path: '/tradingRewards/:campaignId',
          name: 'tradingRewards',
          component: () => import('@/views/promotion/TradingRewards'),
        },
        {
          path: '/tradingContest/:campaignId',
          name: 'tradingContest',
          component: () => import('@/views/promotion/TradingContest'),
          beforeEnter: async (to, from, next) => {
            if (from.name === 'login' || from.name === 'to_login') {
              await store.dispatch('promotion/actionGetEligibleCampaigns')
            }
            if (
              canParticipate(PROMOTION_TYPE.TRADING_CONTEST) &&
              to.params.campaignId &&
              store.state.promotion.eligibleCampaigns.find(c => c.id == to.params.campaignId)
            )
              next()
            else
              next({
                path: '/',
              })
          },
        },
        //prop trading
        {
          name: 'propTradingCreateAccount',
          path: '/propTrading/createAccount',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            allowAnonymous: true,
            notShowDepositHeader: true,
          },
          beforeEnter: async (to, from, next) => {
            const isEligibleForPropTrading = await verifyPropTradingEligibility()

            if (isEligibleForPropTrading) {
              if (store.state.common.countryCode == '6877') return next({ name: 'home' })
              return next()
            }

            return next({ name: from.name ?? 'home' })
          },
        },
        {
          name: 'propTradingAchievement',
          path: '/propTrading/achievement',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            notShowDepositHeader: true,
          },
          beforeEnter: async (to, from, next) => {
            const isEligibleForPropTrading = await verifyPropTradingEligibility()

            if (isEligibleForPropTrading) {
              return next()
            }

            return next({ name: from.name ?? 'home' })
          },
        },
        {
          name: 'propTradingDashboard',
          path: '/propTrading/dashboard',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            notShowDepositHeader: true,
          },
          beforeEnter: async (to, from, next) => {
            const isEligibleForPropTrading = await verifyPropTradingDashboardEligibility()

            if (isEligibleForPropTrading) {
              return next()
            }

            return next({ path: from.fullPath })
          },
        },
        {
          name: 'propTradingMetricsIndex',
          path: '/propTrading/metrics',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            notShowDepositHeader: true,
          },
          redirect: () => ({ name: 'propTradingDashboard' }),
        },
        {
          name: 'propTradingMetrics',
          path: '/propTrading/metrics/:accountNumber',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            notShowDepositHeader: true,
          },
        },
        {
          name: 'propTradingPayout',
          path: '/propTrading/payout/:accountNumber',
          component: () => import('@/views/propTrading/ChildView'),
          meta: {
            restrictBanner: true,
            notShowDepositHeader: true,
          },
        },
        {
          path: '/tradingTool',
          name: 'tradingTool',
          component: () => import('@/views/tradingTools/TradingTools'),
          children: [
            {
              path: 'MARKET_BUZZ',
              name: 'MARKET_BUZZ',
              component: resolve => require(['@/views/tradingTools/MarketBuzz'], resolve),
              meta: {
                restrictBanner: true,
                authority: true,
              },
            },
            {
              path: 'FEAT_FX',
              name: 'FEAT_FX',
              component: resolve => require(['@/views/tradingTools/FeaturedIdea'], resolve),
              meta: {
                restrictBanner: true,
                authority: true,
              },
            },
            {
              path: 'ECON_CALENDAR',
              name: 'ECON_CALENDAR',
              component: resolve => require(['@/views/tradingTools/EconomicCalendar'], resolve),
              meta: {
                restrictBanner: true,
                authority: true,
              },
            },
            {
              path: 'FX_IDEAS',
              name: 'FX_IDEAS',
              component: resolve => require(['@/views/tradingTools/AnalystView'], resolve),
              meta: {
                restrictBanner: true,
                authority: true,
              },
            },
            // {
            //   path: 'HOW_TRADE',
            //   name: 'HOW_TRADE',
            //   component: resolve => require(['@/views/tradingTools/HowToTrade'], resolve),
            //   meta: {
            //     restrictBanner: true,
            //     authority: true,
            //   },
            // },
            {
              path: 'AS_CENTRE',
              name: 'AS_CENTRE',
              component: resolve => require(['@/views/tradingTools/AcuitySignalCentre'], resolve),
              meta: {
                restrictBanner: true,
                authority: true,
              },
            },
          ],
        },
        {
          path: '/home',
          name: 'home',
          component: () => import('@/views/Home'),
          meta: {
            authority: true,
            displayTitle: true,
          },
        },
        {
          path: '/homeDemo',
          name: 'homeDemo',
          component: () => import('@/views/HomeDemo'),
        },
        {
          path: '/liveAccount',
          name: 'liveAccount',
          component: () => import('@/views/Home'),
          meta: {
            authority: true,
            displayTitle: true,
          },
        },
        {
          path: '/openLiveAccount',
          name: 'openLiveAccount',
          component: () => import('@/views/openLiveAccount/index'),
          meta: {
            authority: true,
            restrictBanner: true,
          },
        },
        {
          path: '/openDemoAccount',
          name: 'openDemoAccount',
          component: () => import('@/views/openDemoAccount/index'),
        },
        {
          path: '/openDemoAccount/success',
          name: 'openDemoAccountSuccess',
          component: () => import('@/views/openDemoAccount/Success'),
        },
        {
          path: '/depositFunds',
          name: 'depositFunds',
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs ? import('@/views/deposit/Deposit') : import('@/views/deposit/DepositFunds')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
          meta: {
            authority: true,
          },
        },
        // temporary for testing CPS
        {
          path: '/depositFundsCps',
          name: 'depositFundsCps',
          component: () => import(`@/views/deposit/DepositFundsCps`),
          meta: {
            authority: true,
          },
        },
        {
          path: '/restrictDeposit',
          name: 'restrictDeposit',
          component: () => import(`@/views/deposit/restrictDeposit`),
          meta: {
            noShowLoading: true,
            authority: true,
          },
        },
        {
          path: '/withdrawFunds',
          name: 'withdrawFunds',
          // component: () => import('@/views/withdraw/WithdrawFundsV2'),
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs ? import('@/views/withdraw/WithdrawFundsV2') : import('@/views/withdraw/WithdrawFunds')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
          meta: {
            authority: true,
          },
        },
        {
          path: '/transferFunds',
          name: 'transferFunds',
          component: () => import('@/views/TransferFunds'),
          meta: {
            authority: true,
          },
        },
        {
          path: '/transactionHistory',
          name: 'transactionHistory',
          component: () => import('@/views/TransactionHistory'),
          meta: {
            authority: true,
          },
        },
        {
          path: '/downloads',
          name: 'downloads',
          component: () => import('@/views/Downloads'),
          meta: {
            restrictBanner: true,
            fullWidth: true,
          },
        },
        {
          path: '/myProfile',
          name: 'myProfile',
          component: () => import('@/views/MyProfile'),
        },
        {
          path: '/referFriends',
          name: 'referFriends',
          component: () => import('@/views/promotion/ReferFriend'),
          beforeEnter: (to, from, next) => {
            if (canParticipate(PROMOTION_TYPE.REFER_A_FRIEND_V10)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/referFriendsThai',
          name: 'referFriendsThai',
          component: () => import('@/views/promotion/ReferFriendThai'),
          beforeEnter: (to, from, next) => {
            if (canParticipate(PROMOTION_TYPE.REFER_A_FRIEND_THAILAND)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/depositBonus/:campaignId',
          name: 'dynamicDepositBonus',
          component: () => import('@/views/promotion/DepositBonus'),
          beforeEnter: (to, from, next) => {
            if (
              canParticipate(PROMOTION_TYPE.DEPOSIT_BONUS_V1) &&
              to.params.campaignId &&
              store.state.promotion.eligibleCampaigns.find(c => c.id == to.params.campaignId)
            )
              next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/noDepositBonus/:campaignId',
          name: 'noDepositBonus',
          component: () => import('@/views/promotion/NoDepositBonus'),
          beforeEnter: (to, from, next) => {
            if (
              canParticipate(PROMOTION_TYPE.NO_DEPOSIT_BONUS_3) &&
              to.params.campaignId &&
              store.state.promotion.eligibleCampaigns.find(c => c.id == to.params.campaignId)
            )
              next()
            else
              next({
                path: from.fullPath,
              })
          },
          meta: {
            mobileTitle: 'menu.promotion',
          },
        },
        {
          path: '/promotion/:campaignName/:campaignId',
          name: 'promotion',
          component: () => import('@/views/promotion/index'),
        },
        {
          path: '/redepositBonus',
          name: 'redepositBonus',
          component: () => import('@/views/promotion/RedepositBonus'),
          beforeEnter: (to, from, next) => {
            /**
             * To do
             * Check access from API
             */
            if (canParticipate(PROMOTION_TYPE.RE_DEPOSIT_BONUS)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/welcomeBonus',
          name: 'welcomeBonus',
          component: () => import('@/views/promotion/WelcomeBonus'),
          beforeEnter: (to, from, next) => {
            /**
             * To do
             * Check access from API
             */
            if (canParticipate(PROMOTION_TYPE.WELCOME_BONUS)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/vpsRefund',
          name: 'vpsRefund',
          component: () => import('@/views/promotion/VpsRefund'),
          beforeEnter: (to, from, next) => {
            if (canParticipate(PROMOTION_TYPE.VPS_REFUND)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/luckyDraw/:campaignId',
          name: 'luckyDraw',
          component: () => import('@/views/promotion/LuckyDraw'),
          beforeEnter: (to, from, next) => {
            if (canParticipate(PROMOTION_TYPE.LUCKY_DRAW_V2)) next()
            else
              next({
                path: from.fullPath,
              })
          },
        },
        {
          path: '/dailyNewsletter',
          name: 'dailyNewsletter',
          component: () => import('@/views/promotion/DailyNewsletter'),
        },
        {
          path: '/fundsGrowthPromotion',
          name: 'fundsGrowthPromotion',
          component: () => import('@/views/promotion/FundsGrowthPromotion'),
        },
        {
          path: '/clubBleu',
          name: 'clubBleu',
          component: () => import('@/views/rewards/ClubBleu'),
          meta: {
            restrictBanner: true,
            fullWidth: true,
          },
          beforeEnter: (to, from, next) => {
            /**
             * To do
             * Check access from API
             */
            if (store.state.loyalty.showLoyalty) {
              if (store.state.loyalty.loyaltyInfo && store.state.loyalty.loyaltyInfo.status !== 3) next()
              else if (!store.state.loyalty.loyaltyInfo) next()
              else next({ path: from.fullPath })
            } else next({ path: from.fullPath })
          },
        },
        {
          path: '/lossRecover',
          name: 'lossRecover',
          component: () => import('@/views/promotion/LossRecover'),
        },
        // {
        //   path: '/changePassword',
        //   name: 'changePassword',
        //   component: () => import('@/views/ChangePassword'),
        // },
        {
          path: '/securityManagement',
          name: 'securityManagement',
          component: () => import('@/views/SecurityManagement'),
        },
        {
          path: '/contactUs',
          name: 'contactUs',
          component: () => import('@/views/ContactUs'),
        },
        {
          path: '/myTickets',
          name: 'myTickets',
          component: () => import('@/views/MyTickets'),
        },
        {
          path: '/paymentDetails',
          name: 'paymentDetails',
          component: () => import('@/views/PaymentDetails'),
          meta: {
            authority: true,
          },
        },
        // pages of deposit
        {
          path: '/deposit/cps/:name',
          name: 'cps',
          component: () => import('@/views/deposit/cps/Index'),
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (!access.data) {
              if (isNeedKYC.includes(to.params.name)) {
                const redirectPath = await getIbtRedirectPath()

                if (redirectPath?.length > 0) {
                  next(redirectPath)
                } else {
                  next()
                }
              } else {
                next()
              }
            } else {
              next({ path: from.fullPath })
              endLoading()
            }
          },
        },
        // temporary for testing CPS
        {
          path: '/deposit/cpsIndex/:name',
          name: 'cpsIndex',
          component: () => import('@/views/deposit/cps/IndexCps'),
        },
        {
          path: '/deposit/creditOrDebit',
          name: 'creditOrDebit',
          component: () => import('@/views/deposit/CreditOrDebit'),
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (!access.data) {
              next()
            } else {
              next({ path: from.fullPath })
              endLoading()
            }
          },
        },
        {
          path: '/deposit/appleSolidpay',
          name: 'appleSolidpay',
          component: () => import('@/views/deposit/AppleSolidpay'),
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (!access.data) {
              next()
            } else {
              next({ path: from.fullPath })
              endLoading()
            }
          },
        },
        {
          path: '/deposit/bridgePaymentDepositPCS',
          name: 'bridgePaymentDepositPCS',
          component: () => import('@/views/deposit/pcs/BridgepayDepositPCS'),
          meta: {
            mobileTitle: 'menu.depositFund',
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
        {
          path: '/deposit/checkoutCCPCS',
          name: 'checkoutCCPCS',
          component: () => import('@/views/deposit/pcs/CheckoutCCPCS'),
          meta: {
            mobileTitle: 'menu.depositFund',
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
        // {
        //   path: '/deposit/appleGooglePay',
        //   name: 'appleGooglePay',
        //   component: () => import('@/views/deposit/AppleGooglePay'),
        // },
        // {
        //   path: '/deposit/googlePay',
        //   name: 'googlePay',
        //   component: () => import('@/views/deposit/GooglePay'),
        // },
        // {
        //   path: '/deposit/internationalSwift',
        //   name: 'internationalSwift',
        //   component: () => import('@/views/deposit/InternationalSwift'),
        //   beforeEnter: async (to, from, next) => {
        //     const redirectPath = await getIbtRedirectPath()
        //
        //     if (redirectPath?.length > 0) {
        //       next(redirectPath)
        //     } else {
        //       next()
        //     }
        //   },
        // },
        // {
        //   path: '/deposit/mobilePay',
        //   name: 'mobilePay',
        //   component: () => import('@/views/deposit/MobilePay'),
        // },
        // {
        //   path: '/deposit/unionPay',
        //   name: 'unionPay',
        //   component: () => import('@/views/deposit/UnionPay'),
        // },
        // {
        //   path: '/deposit/unionPayChina',
        //   name: 'unionPayChina',
        //   component: () => import('@/views/deposit/UnionPayChina'),
        // },
        // {
        //   path: '/deposit/fasaPay',
        //   name: 'fasaPay',
        //   component: () => import('@/views/deposit/FasaPay'),
        // },
        // {
        //   path: '/deposit/neteller',
        //   name: 'neteller',
        //   component: () => import('@/views/deposit/NetellerDeposit'),
        // },
        // {
        //   path: '/deposit/skrillPay',
        //   name: 'skrillPay',
        //   component: () => import('@/views/deposit/SkrillPay.vue'),
        // },
        // {
        //   path: '/deposit/vietnamZotapay',
        //   name: 'vietnamZotapay',
        //   component: () => import('@/views/deposit/VietnamZotapay'),
        // },
        // {
        //   path: '/deposit/Crypto',
        //   name: 'Crypto',
        //   component: () => import('@/views/deposit/Crypto'),
        // },
        // {
        //   path: '/deposit/thailand',
        //   name: 'thailand',
        //   component: () => import('@/views/deposit/Thailand'),
        // },
        // {
        //   path: '/deposit/canadaLocalBankDeposit',
        //   name: 'canadaLocalBank',
        //   component: () => import('@/views/deposit/CanadaLocalBankDeposit'),
        // },
        // {
        //   path: '/deposit/uk',
        //   name: 'uk',
        //   component: () => import('@/views/deposit/UK'),
        // },
        // {
        //   path: '/deposit/laos',
        //   name: 'laos',
        //   component: () => import('@/views/deposit/Laos'),
        // },
        // {
        //   path: '/deposit/malaysia',
        //   name: 'malaysia',
        //   component: () => import('@/views/deposit/Malaysia'),
        // },
        // {
        //   path: '/deposit/dasshpeUPI',
        //   name: 'dasshpeUPI',
        //   component: () => import('@/views/deposit/DasshpeUPI'),
        // },
        // {
        //   path: '/deposit/upi',
        //   name: 'upi',
        //   component: () => import('@/views/deposit/Upi'),
        // },
        // {
        //   path: '/deposit/dasshpeWallet',
        //   name: 'dasshpeWallet',
        //   component: () => import('@/views/deposit/DasshpeWallet'),
        // },
        // {
        //   path: '/deposit/indonesia',
        //   name: 'indonesia',
        //   component: () => import('@/views/deposit/Indonesia'),
        // },
        // {
        //   path: '/deposit/hongkong',
        //   name: 'hongkong',
        //   component: () => import('@/views/deposit/HongKong'),
        // },
        // {
        //   path: '/deposit/hongkongQR',
        //   name: 'hongkongQR',
        //   component: () => import('@/views/deposit/HongKongQR'),
        // },
        // {
        //   path: '/deposit/HongKongQR-scanPay',
        //   name: 'HongKongQR-scanPay',
        //   component: () => import('@/views/deposit/HongKongQR-scanPay'),
        // },
        // {
        //   path: '/deposit/pagsmileBrazil',
        //   name: 'pagsmileBrazil',
        //   component: () => import('@/views/deposit/PagsmileBrazil'),
        // },
        // {
        //   path: '/deposit/pagsmile',
        //   name: 'pagsmile',
        //   component: () => import('@/views/deposit/Pagsmile'),
        // },
        // {
        //   path: '/deposit/philipiness',
        //   name: 'philipiness',
        //   component: () => import('@/views/deposit/Philipiness'),
        // },
        // {
        //   path: '/deposit/eu',
        //   name: 'eu',
        //   component: () => import('@/views/deposit/EU'),
        // },
        // {
        //   path: '/deposit/payablNetbanking',
        //   name: 'payablNetbanking',
        //   component: () => import('@/views/deposit/payablNetbanking'),
        // },
        // {
        //   path: '/deposit/nigeria',
        //   name: 'nigeria',
        //   component: () => import('@/views/deposit/Nigeria'),
        // },
        // {
        //   path: '/deposit/perfectMoney',
        //   name: 'perfectMoney',
        //   component: () => import('@/views/deposit/PerfectMoney'),
        // },
        // {
        //   path: '/deposit/localdepositor',
        //   name: 'localdepositor',
        //   component: () => import('@/views/deposit/LocalDepositor'),
        // },
        // {
        //   path: '/deposit/IndiaBankTransfer',
        //   name: 'indiaBankTransfer',
        //   component: () => import('@/views/deposit/IndiaBankTransfer'),
        // },
        // {
        //   path: '/deposit/India',
        //   name: 'india',
        //   component: () => import('@/views/deposit/India'),
        // },
        // {
        //   path: '/deposit/myFPX',
        //   name: 'myFPX',
        //   component: () => import('@/views/deposit/MalaysiaFPX'),
        // },
        // {
        //   path: '/deposit/myEwallet',
        //   name: 'myEWallet',
        //   component: () => import('@/views/deposit/MalaysiaEwallet'),
        // },
        // {
        //   path: '/deposit/japanP2P',
        //   name: 'japanP2P',
        //   component: () => import('@/views/deposit/JapanP2P'),
        // },
        // {
        //   path: '/deposit/bitwalletDeposit',
        //   name: 'bitwalletDeposit',
        //   component: () => import('@/views/deposit/BitwalletDeposit'),
        // },
        // {
        //   path: '/deposit/paystackDeposit',
        //   name: 'paystackDeposit',
        //   component: () => import('@/views/deposit/PaystackDeposit'),
        // },
        // {
        //   path: '/deposit/japan',
        //   name: 'japan',
        //   component: () => import('@/views/deposit/Japan'),
        // },
        // {
        //   name: 'iDeal',
        //   component: () => import('@/views/deposit/iDeal'),
        //   path: '/deposit/iDeal',
        // },
        // payabl netbanking
        {
          path: '/payablNetbanking/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/payablNetbanking/thirdPartyDepositFailed',
          name: 'thirdPartyDepositFailed',
          // component: () => import('@/views/callback/ThirdPartyDepositFailed'),
          meta: {
            restrictReload: true,
          },
        },
        // {
        //   path: '/deposit/VietnamBankTransfer',
        //   name: 'vietnamBankTransfer',
        //   component: () => import('@/views/deposit/VietnamBankTransfer'),
        // },
        // {
        //   name: 'interac',
        //   path: '/deposit/interac',
        //   component: () => import('@/views/deposit/Interac'),
        // },
        // {
        //   name: 'interacGigadat',
        //   path: '/deposit/interacGigadat',
        //   component: () => import('@/views/deposit/InteracGigadat'),
        // },
        // pages of thirdParty
        {
          path: '/zotapay/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/unionpay/thirdPartyDepositResult',
          name: 'unionpayThirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/creditcard/thirdPartyDepositResult',
          name: 'creditcardThirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/fasapay/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/skrill/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/skrill/thirdPartyDepositFailed',
          name: 'thirdPartyDepositFailed',
          component: () => import('@/views/callback/ThirdPartyDepositFailed'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/xpay/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult.vue'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/crypto/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/crypto/thirdPartyDepositFailed',
          name: 'thirdPartyDepositFailed',
          component: () => import('@/views/callback/ThirdPartyDepositFailed'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/cryptousdt/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/cryptousdt/thirdPartyDepositFailed',
          name: 'thirdPartyDepositFailed',
          component: () => import('@/views/callback/ThirdPartyDepositFailed'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/mijipay/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/paytoday/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/paysafe_neteller/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/gigadat/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/gigadat/thirdPartyDepositFailed',
          name: 'thirdPartyDepositFailed',
          component: () => import('@/views/callback/ThirdPartyDepositFailed'),
          meta: {
            restrictReload: true,
          },
        },
        // virtual pay
        {
          path: '/paywize/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/thaipay/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/solid/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/pagsmile/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        {
          path: '/bitwallet/thirdPartyDepositResult',
          name: 'thirdPartyDepositResult',
          component: () => import('@/views/callback/ThirdPartyDepositResult'),
          meta: {
            restrictReload: true,
          },
        },
        // {
        //   path: '/cps/thirdPartyDepositResult',
        //   name: 'cpsThirdPartyDepositResult',
        //   component: () => import('@/views/callback/ThirdPartyDepositResult'),
        //   meta: {
        //     restrictReload: true,
        //   },
        // },
        {
          path: '/thirdPartyDepositResult/:id',
          name: 'thirdPartyResult',
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs
              ? import('@/views/callback/ThirdPartyDepositResultPCS')
              : import('@/views/callback/ThirdPartyDepositResult')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
        {
          path: '/thirdPartyDepositFailed/:id',
          name: 'thirdPartyFailed',
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs
              ? import('@/views/callback/ThirdPartyDepositResultPCS')
              : import('@/views/callback/ThirdPartyDepositResult')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
        {
          path: '/:id/thirdPartyDepositResult',
          name: 'thirdPartyResult',
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs
              ? import('@/views/callback/ThirdPartyDepositResultPCS')
              : import('@/views/callback/ThirdPartyDepositResult')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
        {
          path: '/:id/thirdPartyDepositFailed',
          name: 'thirdPartyFailed',
          component: () => {
            const isUsePcs = store.state.payment.isUsePcs
            return isUsePcs
              ? import('@/views/callback/ThirdPartyDepositResultPCS')
              : import('@/views/callback/ThirdPartyDepositResult')
          },
          beforeEnter: async (to, from, next) => {
            const access = await store.dispatch('payment/actionSetIsUsePcs')
            if (access.code == 0) next()
          },
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/views/shared/LoginLayout'),
      children: [
        {
          path: '/login',
          name: 'login',
          component: () => import('@/views/LoginClient'),
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
          },
        },
        {
          path: '/login/to_login',
          name: 'to_login',
          component: () => import('@/views/LoginClient'),
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
          },
        },
        {
          path: '/addAuthentication',
          name: 'addAuthentication',
          component: () => import('@/views/LoginClient'),
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
          },
        },
        {
          path: '/verifyAuthentication',
          name: 'verifyAuthentication',
          component: () => import('@/views/LoginClient'),
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
          },
        },
        {
          path: '/forgetPassword',
          name: 'forgetPassword',
          component: () => import('@/views/ForgetPasswordReq'),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: '/forgetPasswordMobile',
          name: 'forgetPasswordMobile',
          component: () => import('@/views/ForgetPasswordMobileReq'),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: '/resetProfilePassword',
          name: 'resetProfilePassword',
          component: () => import('@/views/ResetProfilePassword'),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: '/resetPassword',
          name: 'resetPassword',
          component: () => import('@/views/ResetPassword'),
          meta: {
            allowAnonymous: true,
            showDepositHeader: true,
          },
        },
        {
          path: '/resetPasswordMobile',
          name: 'resetPasswordMobile',
          component: () => import('@/views/ResetPasswordMobile'),
          meta: {
            allowAnonymous: true,
            showDepositHeader: true,
          },
        },
        {
          path: '/forceChangePassword',
          name: 'forceChangePassword',
          component: () => import('@/views/ForceChangePassword'),
          meta: {
            allowAnonymous: true,
            showDepositHeader: true,
          },
        },
        {
          path: '/ptResetPassword',
          name: 'ptResetPassword',
          component: () => import('@/views/PtResetPassword'),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: '/error',
          name: 'error',
          component: () => import('@/views/register/Error'),
          meta: {
            allowAnonymous: true,
            noShowLoading: true,
            showDepositHeader: true,
          },
        },
      ],
    },
    {
      path: '',
      component: () => import('@/views/shared/Layout'),
      children: [
        {
          name: '404',
          path: '*',
          component: () => import('@/views/404'),
        },
      ],
    },
  ],
})
export default router
identity.authorize()
